/**
 * Audit API service — report generation and retrieval.
 */

export interface AuditReportFilters {
  dateRange?: { start: string; end: string }
  eventTypes?: string[]
  studyIds?: string[]
  userIds?: string[]
  resourceIds?: string[]
}

export interface CreateAuditReportRequest {
  filters: AuditReportFilters
  format: "csv" | "html"
}

interface AuditReportJob {
  reportId: string
  status: "IN_PROGRESS" | "COMPLETE" | "FAILED"
  createdAt: string
  completedAt?: string
  downloadUrl?: string
}

export interface AuditReportListItem {
  reportId: string
  status: "IN_PROGRESS" | "COMPLETE" | "FAILED"
  createdAt: string
  filters: AuditReportFilters
  format: string
}

interface ListAuditReportsResponse {
  reports: AuditReportListItem[]
  nextToken?: string
}

export const auditApiService = {
  createReport: async (
    request: CreateAuditReportRequest,
    idempotencyKey: string,
  ): Promise<AuditReportJob> => {
    const response = await fetch("/api/v1/audits/reports", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Idempotency-Key": idempotencyKey,
        Authorization: `Bearer ${await getToken()}`,
      },
      body: JSON.stringify(request),
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new AuditApiError(body.error || `Request failed: ${response.status}`, response.status)
    }
    return response.json()
  },

  getReport: async (reportId: string): Promise<AuditReportJob> => {
    const response = await fetch(`/api/v1/audits/reports/${reportId}`, {
      headers: {
        Authorization: `Bearer ${await getToken()}`,
      },
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new AuditApiError(body.error || `Request failed: ${response.status}`, response.status)
    }
    return response.json()
  },

  listReports: async (options?: {
    maxResults?: number
    nextToken?: string
  }): Promise<ListAuditReportsResponse> => {
    const params = new URLSearchParams()
    if (options?.maxResults) params.set("maxResults", String(options.maxResults))
    if (options?.nextToken) params.set("nextToken", options.nextToken)
    const qs = params.toString()
    const url = `/api/v1/audits/reports${qs ? `?${qs}` : ""}`

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${await getToken()}`,
      },
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new AuditApiError(body.error || `Request failed: ${response.status}`, response.status)
    }
    return response.json()
  },
}

export class AuditApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message)
    this.name = "AuditApiError"
  }
}

async function getToken(): Promise<string> {
  const { getApiToken } = await import("./apiToken")
  return getApiToken()
}
